/**
 * Cliente HTTP do painel.
 *
 * Segurança (`../backend/docs/SEGURANCA.md`):
 * - O access token **nunca** é persistido: sai da closure de `lib/auth.ts` a cada requisição. Não
 *   existe (e não pode passar a existir) `localStorage`/`sessionStorage` neste arquivo.
 * - `withCredentials: true` para o cookie httpOnly `triplice_refresh` viajar sozinho.
 * - `X-CSRF-Token` (double-submit com o cookie legível `triplice_csrf`) só nas rotas
 *   `POST /auth/refresh` e `POST /auth/logout`, que são as que o backend exige.
 * - 401 dispara **um** refresh silencioso; as requisições concorrentes esperam a mesma promessa
 *   (fila) em vez de disparar N refreshes.
 */
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  avisarNovaSenhaObrigatoria,
  encerrarSessaoLocal,
  definirAccessToken,
  MENSAGEM_NOVA_SENHA_OBRIGATORIA,
  obterAccessToken,
} from './auth';
import type { CorpoErroApi, ErroDeCampo, MetaPaginacao, RespostaApi } from '@/tipos/api';

export const COOKIE_CSRF = 'triplice_csrf';
export const HEADER_CSRF = 'X-CSRF-Token';
/** 404 sem explicação do servidor. Quem mostra o erro usa isto para saber que não há o que citar. */
export const MENSAGEM_404_PADRAO = 'Não encontrado.';
const TIMEOUT_MS = 30_000;

/** Rotas que exigem o header de CSRF (double-submit). */
const ROTAS_CSRF = ['/auth/refresh', '/auth/logout'] as const;

/** Requisição já repetida depois de um refresh — evita laço infinito de 401. */
interface ConfigInterna extends InternalAxiosRequestConfig {
  _jaRepetida?: boolean;
}

/** Erro normalizado que as telas e o TanStack Query recebem. */
export class ErroApi extends Error {
  readonly statusCode: number;
  /** Erros por campo (422) — prontos para `setError` do react-hook-form. */
  readonly erros: ErroDeCampo[];
  /** Segundos até poder tentar de novo (429, do header `Retry-After`). */
  readonly retryAfter?: number;

  constructor(params: {
    statusCode: number;
    mensagem: string;
    erros?: ErroDeCampo[];
    retryAfter?: number;
  }) {
    super(params.mensagem);
    this.name = 'ErroApi';
    this.statusCode = params.statusCode;
    this.erros = params.erros ?? [];
    this.retryAfter = params.retryAfter;
  }

  get ehValidacao(): boolean {
    return this.statusCode === 422;
  }

  get ehLimiteDeTaxa(): boolean {
    return this.statusCode === 429;
  }
}

// ---------------------------------------------------------------------------
// Cookies (leitura apenas do cookie legível de CSRF — o de sessão é httpOnly)
// ---------------------------------------------------------------------------

export function lerCookie(nome: string): string | null {
  if (typeof document === 'undefined') return null;
  const alvo = `${encodeURIComponent(nome)}=`;
  for (const parte of document.cookie.split(';')) {
    const item = parte.trim();
    if (item.startsWith(alvo)) return decodeURIComponent(item.slice(alvo.length));
  }
  return null;
}

function ehRotaCsrf(url: string | undefined): boolean {
  if (!url) return false;
  const caminho = url.split('?')[0];
  return ROTAS_CSRF.some((rota) => caminho === rota || caminho.endsWith(rota));
}

// ---------------------------------------------------------------------------
// Interceptor de requisição (exportado para teste unitário, sem rede)
// ---------------------------------------------------------------------------

export function aplicarCabecalhosDeRequisicao(config: InternalAxiosRequestConfig) {
  const cabecalhos = AxiosHeaders.from(config.headers);

  const token = obterAccessToken();
  if (token) cabecalhos.set('Authorization', `Bearer ${token}`);

  const metodo = (config.method ?? 'get').toLowerCase();
  if (metodo === 'post' && ehRotaCsrf(config.url)) {
    const csrf = lerCookie(COOKIE_CSRF);
    if (csrf) cabecalhos.set(HEADER_CSRF, csrf);
  }

  config.headers = cabecalhos;
  return config;
}

// ---------------------------------------------------------------------------
// Interceptor de resposta: desempacota `{ data }` / `{ data, meta }`
// ---------------------------------------------------------------------------

function temEnvelope(corpo: unknown): corpo is RespostaApi<unknown> {
  return typeof corpo === 'object' && corpo !== null && 'data' in corpo;
}

export function desempacotarResposta<T = unknown>(resposta: AxiosResponse): AxiosResponse<T> {
  if (temEnvelope(resposta.data)) {
    const envelope = resposta.data;
    resposta.data = envelope.data;
    (resposta as AxiosResponse<T> & { meta?: MetaPaginacao }).meta = envelope.meta;
  }
  return resposta as AxiosResponse<T>;
}

/** `meta` de uma resposta paginada (o interceptor a guarda ao desempacotar). */
export function metaDe(resposta: AxiosResponse): MetaPaginacao | undefined {
  return (resposta as AxiosResponse & { meta?: MetaPaginacao }).meta;
}

// ---------------------------------------------------------------------------
// Normalização de erro
// ---------------------------------------------------------------------------

function ehCorpoDeErro(corpo: unknown): corpo is Partial<CorpoErroApi> {
  return typeof corpo === 'object' && corpo !== null;
}

/**
 * Todo erro — de rede, de timeout, do servidor — sai daqui como `ErroApi` com mensagem em pt-BR.
 * A mensagem do **servidor** sempre tem precedência: ela é a explicação concreta ("Papel em uso por
 * 3 usuários"), e o texto por status é só o fallback de quando não houve explicação.
 */
export function normalizarErro(erro: unknown): ErroApi {
  if (erro instanceof ErroApi) return erro;

  if (!(erro instanceof AxiosError)) {
    return new ErroApi({ statusCode: 0, mensagem: 'Erro inesperado. Tente novamente.' });
  }

  if (!erro.response) {
    const mensagem =
      erro.code === 'ECONNABORTED'
        ? 'A requisição demorou demais. Tente novamente.'
        : 'Não foi possível falar com o servidor. Verifique sua conexão.';
    return new ErroApi({ statusCode: 0, mensagem });
  }

  const { status, data, headers } = erro.response;
  const corpo = ehCorpoDeErro(data) ? data : {};
  const erros = Array.isArray(corpo.erros) ? corpo.erros : [];

  let retryAfter: number | undefined;
  if (status === 429) {
    const bruto = headers?.['retry-after'] ?? headers?.['Retry-After'];
    const segundos = Number(bruto);
    if (Number.isFinite(segundos) && segundos >= 0) retryAfter = segundos;
  }

  // O contrato do backend é `message` (nome do `HttpException` do Nest), não `mensagem`.
  let mensagem = typeof corpo.message === 'string' ? corpo.message : '';
  if (!mensagem) {
    if (status === 422) mensagem = 'Confira os campos destacados.';
    else if (status === 429)
      mensagem =
        retryAfter !== undefined
          ? `Muitas tentativas. Tente de novo em ${retryAfter}s.`
          : 'Muitas tentativas. Tente de novo em instantes.';
    else if (status === 401) mensagem = 'Sua sessão expirou. Entre novamente.';
    else if (status === 403) mensagem = 'Você não tem permissão para isso.';
    else if (status === 404) mensagem = MENSAGEM_404_PADRAO;
    else mensagem = 'Erro no servidor. Tente novamente.';
  }

  return new ErroApi({ statusCode: status, mensagem, erros, retryAfter });
}

// ---------------------------------------------------------------------------
// Instância
// ---------------------------------------------------------------------------

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: TIMEOUT_MS,
  headers: { Accept: 'application/json' },
});

api.interceptors.request.use(aplicarCabecalhosDeRequisicao);

// ---------------------------------------------------------------------------
// Refresh silencioso com fila (uma única chamada para N requisições em 401)
// ---------------------------------------------------------------------------

let refreshEmAndamento: Promise<string> | null = null;

/** `POST /auth/refresh`. Concorrentes compartilham a mesma promessa, em vez de N refreshes. */
export function renovarSessao(): Promise<string> {
  refreshEmAndamento ??= api
    .post<{ accessToken: string }>('/auth/refresh')
    .then((resposta) => {
      const token = resposta.data?.accessToken;
      if (!token) throw new ErroApi({ statusCode: 401, mensagem: 'Sessão expirada.' });
      definirAccessToken(token);
      return token;
    })
    .finally(() => {
      refreshEmAndamento = null;
    });
  return refreshEmAndamento;
}

api.interceptors.response.use(
  (resposta) => desempacotarResposta(resposta),
  async (erro: unknown) => {
    const original =
      erro instanceof AxiosError ? (erro.config as ConfigInterna | undefined) : undefined;
    const status = erro instanceof AxiosError ? erro.response?.status : undefined;

    /*
     * `_jaRepetida` é a flag anti-laço: sem ela, um refresh que devolve token inválido faria a
     * requisição original falhar com 401 de novo, pedir outro refresh, e assim por diante.
     * `ehRotaCsrf` exclui o próprio `/auth/refresh`: um 401 ali significa que o cookie morreu, e
     * tentar renovar com ele seria pedir a mesma coisa que acabou de ser negada.
     */
    const podeRenovar =
      status === 401 && original && !original._jaRepetida && !ehRotaCsrf(original.url);

    if (podeRenovar) {
      original._jaRepetida = true;
      try {
        await renovarSessao();
        return await api.request(original);
      } catch {
        // Refresh falhou: some com o token da memória e avisa o app (vai para o login).
        encerrarSessaoLocal();
        throw normalizarErro(erro);
      }
    }

    if (status === 401 && ehRotaCsrf(original?.url)) encerrarSessaoLocal();

    const normalizado = normalizarErro(erro);
    // O admin exigiu senha nova com a pessoa no meio do uso (1.8): o app relê a sessão e vai à tela
    // bloqueante. O erro segue para quem chamou, que mostra a mensagem enquanto o desvio acontece.
    if (status === 403 && normalizado.message === MENSAGEM_NOVA_SENHA_OBRIGATORIA) {
      avisarNovaSenhaObrigatoria();
    }
    throw normalizado;
  },
);
